import { EventDrivenStore } from '@core/store/eventDrivenStore';
import constants from '@core/utils/constants';

class InboxStore extends EventDrivenStore {
  constructor() {
    super('inbox', {
      notifications: {
        items: [],
        filteredItems: [],
        loading: false,
        error: null
      },
      filters: {
        type: 'all',
        readStatus: 'all'
      },
      stats: {
        total: 0,
        unread: 0
      },
      actions: {
        markingRead: false,
        deleting: false
      }
    }, {
      persist: true,
      persistKey: 'inbox_notifications',
      persistPaths: ['notifications.items']
    });

    this.setupEvents();
    this.loadFromPersistedStorage();
  }

  // Load notifications from localStorage and merge with API data
  loadFromPersistedStorage() {
    try {
      const saved = localStorage.getItem('inbox_notifications');
      if (saved) {
        const data = JSON.parse(saved);
        if (data.notifications?.items && Array.isArray(data.notifications.items)) {
          // Restore read/unread status from localStorage
          this.state.notifications.items = data.notifications.items;
          this.state.notifications.filteredItems = this.filterNotifications(this.state.notifications.items);
          this.updateStats();
        }
      }
    } catch (error) {
      console.warn('Failed to load notifications from localStorage:', error);
    }
  }

  // Save current state to localStorage
  saveToPersistedStorage() {
    try {
      const dataToSave = {
        notifications: {
          items: this.state.notifications.items
        }
      };
      localStorage.setItem('inbox_notifications', JSON.stringify(dataToSave));
    } catch (error) {
      console.warn('Failed to save notifications to localStorage:', error);
    }
  }

  // Get persisted read status from localStorage
  getPersistedReadStatus() {
    try {
      const saved = localStorage.getItem('inbox_notifications');
      if (saved) {
        const data = JSON.parse(saved);
        return data.notifications?.items || [];
      }
    } catch (error) {
      console.warn('Failed to get persisted read status:', error);
    }
    return [];
  }

  setupEvents() {
    // ============ NOTIFICATIONS LOAD EVENTS ============
    this.on(constants.EVENTS.INBOX.NOTIFICATIONS_LOADING, () => {
      this.state.notifications.loading = true;
      this.state.notifications.error = null;
    });

    this.on(constants.EVENTS.INBOX.NOTIFICATIONS_LOADED, (data) => {
      const notifications = Array.isArray(data) ? data : [];

      // Merge with localStorage data to preserve read/unread status
      const saved = this.getPersistedReadStatus();
      const mergedNotifications = notifications.map(notification => {
        const savedNotification = saved.find(n => n.id === notification.id);
        if (savedNotification) {
          // Preserve the read status from localStorage
          return {
            ...notification,
            read: savedNotification.read,
            readAt: savedNotification.readAt
          };
        }
        return notification;
      });

      this.state.notifications.items = mergedNotifications;
      this.state.notifications.filteredItems = this.filterNotifications(mergedNotifications);
      this.state.notifications.loading = false;
      this.state.notifications.error = null;
      this.updateStats();
      this.saveToPersistedStorage();
    });

    this.on(constants.EVENTS.INBOX.NOTIFICATIONS_LOAD_ERROR, (error) => {
      this.state.notifications.loading = false;
      this.state.notifications.error = error?.message || error;
      this.state.notifications.items = [];
      this.state.notifications.filteredItems = [];
    });

    // ============ MARK READ EVENTS ============
    this.on(constants.EVENTS.INBOX.NOTIFICATION_MARKING_READ, () => {
      this.state.actions.markingRead = true;
    });

    this.on(constants.EVENTS.INBOX.NOTIFICATION_MARKED_READ, (notificationId) => {
      const notification = this.state.notifications.items.find(n => n.id === notificationId);
      if (notification) {
        notification.read = true;
        notification.readAt = new Date().toISOString();
        this.state.notifications.filteredItems = this.filterNotifications(this.state.notifications.items);
        this.updateStats();
        this.saveToPersistedStorage();
      }
      this.state.actions.markingRead = false;
    });

    this.on(constants.EVENTS.INBOX.NOTIFICATION_MARK_READ_ERROR, (error) => {
      this.state.actions.markingRead = false;
      console.error('Failed to mark notification as read:', error);
    });

    // ============ MARK ALL READ EVENTS ============
    this.on(constants.EVENTS.INBOX.NOTIFICATION_MARKING_ALL_READ, () => {
      this.state.actions.markingRead = true;
    });

    this.on(constants.EVENTS.INBOX.NOTIFICATION_MARKED_ALL_READ, () => {
      this.state.notifications.items.forEach(notification => {
        notification.read = true;
        notification.readAt = new Date().toISOString();
      });
      this.state.notifications.filteredItems = this.filterNotifications(this.state.notifications.items);
      this.updateStats();
      this.saveToPersistedStorage();
      this.state.actions.markingRead = false;
    });

    this.on(constants.EVENTS.INBOX.NOTIFICATION_MARK_ALL_READ_ERROR, (error) => {
      this.state.actions.markingRead = false;
      console.error('Failed to mark all notifications as read:', error);
    });

    // ============ DELETE EVENTS ============
    this.on(constants.EVENTS.INBOX.NOTIFICATION_DELETING, () => {
      this.state.actions.deleting = true;
    });

    this.on(constants.EVENTS.INBOX.NOTIFICATION_DELETED, (notificationId) => {
      this.state.notifications.items = this.state.notifications.items.filter(n => n.id !== notificationId);
      this.state.notifications.filteredItems = this.filterNotifications(this.state.notifications.items);
      this.updateStats();
      this.saveToPersistedStorage();
      this.state.actions.deleting = false;
    });

    this.on(constants.EVENTS.INBOX.NOTIFICATION_DELETE_ERROR, (error) => {
      this.state.actions.deleting = false;
      console.error('Failed to delete notification:', error);
    });

    // ============ FILTER CHANGE EVENTS ============
    this.on(constants.EVENTS.INBOX.NOTIFICATION_FILTER_CHANGE, (filters) => {
      this.state.filters = { ...this.state.filters, ...filters };
      this.state.notifications.filteredItems = this.filterNotifications(this.state.notifications.items);
    });
  }

  // Filter notifications based on current filters
  filterNotifications(notifications) {
    let filtered = [...notifications];

    // Filter by type
    if (this.state.filters.type !== 'all') {
      filtered = filtered.filter(n => n.type === this.state.filters.type);
    }

    // Filter by read status
    if (this.state.filters.readStatus === 'unread') {
      filtered = filtered.filter(n => !n.read);
    } else if (this.state.filters.readStatus === 'read') {
      filtered = filtered.filter(n => n.read);
    }

    // Sort by date (newest first)
    filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt || 0);
      const dateB = new Date(b.createdAt || 0);
      return dateB - dateA;
    });

    return filtered;
  }

  // Update statistics
  updateStats() {
    this.state.stats.total = this.state.notifications.items.length;
    this.state.stats.unread = this.state.notifications.items.filter(n => !n.read).length;
  }

  // Getters
  get notifications() {
    return this.state.notifications.filteredItems;
  }

  get allNotifications() {
    return this.state.notifications.items;
  }

  get unreadCount() {
    return this.state.stats.unread;
  }

  get totalCount() {
    return this.state.stats.total;
  }

  get isLoading() {
    return this.state.notifications.loading;
  }

  get hasError() {
    return !!this.state.notifications.error;
  }

  get error() {
    return this.state.notifications.error;
  }

  get isMarkingRead() {
    return this.state.actions.markingRead;
  }

  get isDeleting() {
    return this.state.actions.deleting;
  }

  get currentFilter() {
    return this.state.filters.type;
  }

  get hasUnread() {
    return this.state.stats.unread > 0;
  }

  // Get notifications grouped by date (today, yesterday, older)
  get groupedNotifications() {
    const groups = {
      today: [],
      yesterday: [],
      older: []
    };

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    this.notifications.forEach(notification => {
      const notificationDate = new Date(notification.createdAt);
      if (notificationDate >= today) {
        groups.today.push(notification);
      } else if (notificationDate >= yesterday) {
        groups.yesterday.push(notification);
      } else {
        groups.older.push(notification);
      }
    });

    return groups;
  }
}

export default InboxStore;
